<!--
    This component is created by LIU student group TDP032
    in our developer portal https://developer.zoezi.se/#/show/c0214d2da7db2807 -> Components -> Fält till formulär
-->

<template>
    <!-- We need to display different things depending on the field, since this field component can be different types of fields we need to check
        the type and then display accordingly -->

    <!-- Displays textarea which is field "Fråga (långt svar)" -->
    <div>
        <v-textarea
            class="field"
            v-if="type == 'textarea'"
            v-model="value"
            :label="translatedText"
            :prepend-icon="getIcon()"
            rows="2"
            :rules="getFieldRules()"
            required
            counter="3000"
        >
            <template v-if="required || (!required && value != '')" #append>
                <v-icon small :color="valid ? 'success' : 'error'">{{
                    getRequired()
                }}</v-icon>
            </template>
        </v-textarea>

        <!-- Displays switch which is field "Ja/Nej" -->
        <div :class="['form-field-switch', { 'form-field-switch--no-icon': !showIcon }]" v-else-if="type == 'yesno'">
            <v-icon v-if="showIcon">{{ getIcon() }}</v-icon>
            <p class="form-field-label">{{ translatedText }}</p>
            <v-switch
                :rules="getFieldRules()"
                required
                v-model="value"
            >
            </v-switch>
        </div>

        <!-- Displays rating which is field "Betyg" -->
        <div v-else-if="type == 'rating'">
            <p class="form-field-label">{{ translatedText }}</p>
            <v-radio-group
                row
                :prepend-icon="getIcon()"
                class="form-field-rating"
                v-model="value"
            >
                <v-icon>mdi-numeric-1</v-icon>
                <span class="rating-section">
                    <v-radio
                        v-for="(item, index) in 10"
                        :key="index"
                        :value="item"
                    ></v-radio>
                </span>
                <v-icon>mdi-numeric-10</v-icon>
                <template v-if="required || (!required && value != '')" #append>
                    <v-icon small :color="valid ? 'success' : 'error'">{{
                        getRequired()
                    }}</v-icon>
                </template>
            </v-radio-group>
        </div>

        <PhoneField
            v-else-if="type == 'phone'"
            v-model="value"
            :label="translatedText"
            :icon="getIcon()"
            :rules="getFieldRules()"
            :starRequired="!!getRequired()"
        />

        <!-- Displays text-field which can be field "Email", "Telefonnummer", "Personnummer", "För- & efternamn" or "Fråga (kort svar)"-->
        <v-text-field
            class="field"
            v-else
            v-model="value"
            :counter="type == 'text' ? 300 : null"
            :label="translatedText"
            :prepend-icon="getIcon()"
            :rules="getFieldRules()"
            required
        >
            <template v-if="required || (!required && value != '')" #append>
                <v-icon small :color="valid ? 'success' : 'error'">{{
                    getRequired()
                }}</v-icon>
            </template>
        </v-text-field>
    </div>
</template>

<script>
import { isValidMail, isValidPersonalCode } from "../util.js";
import PhoneField from "./PhoneField/PhoneField.vue";

export default {
    name: "zoezi-custom-form-field",

    components: {
        PhoneField
    },

    zoezi: {
        title: "Field for customizable form",
        icon: "mdi-form-textbox",
    },

    props: {
        id: {
            type: String,
        },
        type: {
            title: "Field type",
            type: String,
            fulltype: "select",
            typeinfo: {
                multiple: false,
                placeholder: "Select option",
                text: "name",
                value: "id",
                getItems: () => [
                    { name: "First & last name", id: "name" },
                    { name: "E-mail", id: "email" },
                    { name: "Phone number", id: "phone" },
                    { name: "Personal code number", id: "person" },
                    { name: "Question (short answer)", id: "text" },
                    { name: "Question (long answer)", id: "textarea" },
                    { name: "Rating", id: "rating" },
                    { name: "Yes or no", id: "yesno" },
                ],
            },
            default: () => "text",
        },
        text: {
            title: "Title of field",
            type: String,
            default: "New field",
        },

        required: {
            title: "Required",
            type: Boolean,
            default: true,
        },

        translate: {
            title: "Translate",
            type: Boolean,
            default: true,
        },
        showIcon: {
            title: "Show icon",
            type: Boolean,
            default: true,
        }
    },

    data: () => ({
        value: null,
        iconColor: "error",
        valid: false,
    }),

    computed: {
        translatedText() {
            return this.translate ? this.$translate(this.text) : this.text;
        },
    },

    methods: {
        getIcon() {
            /**
                 * Based on the chosen field type, retrieves a corresponding MDI icon.
                 *
                 * @returns {string} The MDI icon.
                 */

            switch (this.type) {
                case "text":
                    return "mdi-message";
                case "textarea":
                    return "mdi-message";
                case "email":
                    return "mdi-email";
                case "name":
                    return "mdi-account";
                case "phone":
                    return "mdi-cellphone";
                case "rating":
                    return "mdi-star-half-full";
                case "person":
                    return "mdi-numeric";
                case "yesno":
                    return "mdi-toggle-switch-off";
            }
        },
        getRequired() {
            /**
                 * Checks if the field's value is valid based on the rules for the specific field type.
                 * If valid, a checkmark icon is returned to indicate validity. If not valid, a required icon is returned.
                 *
                 * @returns {string} The MDI icon.
                 */
            if (!this.valid) {
                return "mdi-asterisk";
            } else if (this.valid) {
                return "mdi-check-bold";
            }
        },
        getFieldRules() {
            /**
                 * Based on the type, return its specific rule.
                 *
                 * @returns {Array} The rule or an empty array if not required.
                 */

            if (this.value != "" || this.required) {
                switch (this.type) {
                    case "text":
                        return [
                            (v) => !!v || this.$translate("{0} is required").format(this.$translate("Message")),
                            (v) => v?.length <= 300 || this.$translate("{0} can't be greater than {1} characters").format(this.$translate("The message"), "300"),
                        ];
                    case "textarea":
                        return [
                            (v) => !!v || this.$translate("{0} is required").format(this.$translate("Message")),
                            (v) => v?.length <= 3000 || this.$translate("{0} can't be greater than {1} characters").format(this.$translate("The message"), "3000"),
                        ];
                    case "email":
                        return [
                            (v) => !!v || this.$translate("{0} is required").format(this.$translate("E-mail")),
                            (v) => isValidMail(v) || this.$translate("{0} must be valid").format(this.$translate("E-mail")),
                        ];
                    case "name":
                        return [(v) => !!v || this.$translate("{0} is required").format(this.$translate("Name"))]
                    case "phone":
                        return [
                            (v) => !!v || this.$translate("{0} is required").format(this.$translate("Phone number")),
                            (v) => !!v || this.$translate("{0} must be valid").format(this.$translate("Phone number")),
                        ]
                    case "rating":
                        return [(v) => !!v || this.$translate("{0} is required").format(this.$translate("Rating"))];
                    case "person":
                        return [
                            (v) => !!v || this.$translate("{0} is required").format(this.$translate("Personal code number")),
                            (v) =>
                                isValidPersonalCode(v) ||
                                this.$translate("{0} must be valid").format(this.$translate("Personal code number")),
                        ]
                }
            }
            return []; // Return an empty array if not required
        },
        setInitialValue() {
            if (this.type == 'yesno') {
                this.value = false;
            } else if (this.type == 'rating') {
                this.value = 0;
            } else {
                this.value = "";
            }
        },
    },

    mounted() {
        this.getRequired();
        this.setInitialValue();
        this.$emit("action", "componentReady", this)
    },

    destroyed() {
        this.$emit("action", "componentDestroyed", this)
    },

    watch: {
        value: {
            immediate: true,
            handler(value) {
                this.valid = this.getFieldRules().every(
                    (rule) => rule(value) == true
                );

                this.$emit("action", "fieldChange", {
                    emitter: this,
                    content: value,
                });
            },
        },
    },
};
</script>

<style>
.field .v-input__append-inner .v-input__icon .v-icon {
    font-size: 1rem;
}

.form-field-rating .v-input--radio-group__input {
    display: grid;
    grid-template-columns: min-content auto min-content;
}

.rating-section {
    display: flex;
    justify-content: space-between;
    width: 100%;
}

.form-field-rating .v-label {
    font-size: 1rem !important;
    display: block;
    max-width: fit-content;
}

.form-field-rating .v-radio,
.form-field-rating .v-input--selection-controls__input {
    margin: 0 !important;
}

.form-field-label {
    font-size: 1rem;
    margin-bottom: 0 !important;
    margin-top: 1rem;
    margin-left: 2rem;
    color: rgba(0, 0, 0, 0.6);
}

.form-field-rating {
    margin-top: 0;
}

.form-field-switch {
    display: grid;
    grid-template-columns: min-content auto max-content;
}

.form-field-switch--no-icon {
    grid-template-columns: auto max-content;
}

.form-field-switch .form-field-label {
    margin-left: 10px;
    line-height: 2rem;
}

.form-field-switch--no-icon .form-field-label {
    margin-left: 0;
}
</style>
